import {
  CheckBadgeIcon,
  HomeIcon,
  ShieldCheckIcon,
} from "@heroicons/react/24/outline"
import Divided from "components/organisms/common/Divided"
import { Disclosured } from "components/organisms/common/DisclosureList"
import useTranslations from "hooks/useTranslations"
import { FaqImage } from "./images"

/**
 * One icon per question, in order: access at home, data privacy, safeguarding
 * standards. Disclosured paints item icons white, which barely shows on the
 * mint buttons of the "light" style, so each is swapped to the question text's
 * near-black.
 */
const darkIcon =
  (Icon: typeof HomeIcon) =>
  ({ className }: { className: string }) => (
    <Icon
      aria-hidden="true"
      className={className.replace("text-white", "text-zinc-900")}
    />
  )

const ICONS = [HomeIcon, ShieldCheckIcon, CheckBadgeIcon].map(darkIcon)

/**
 * Static, like the UK schools FAQ: there is no en-us parent FAQ content in
 * Prismic to filter by `show_on_pages`.
 */
export default function ParentInfoFaq() {
  const { parentInfoFaqTitle, parentInfoFaqQuestions, parentInfoFaqAnswers } =
    useTranslations()

  const questions: string[] = parentInfoFaqQuestions || []
  const answers: string[] = parentInfoFaqAnswers || []
  const list = questions.map((name, index) => ({
    name,
    description: answers[index] || "",
    icon: ICONS[index],
  }))

  return (
    <Divided
      Image={FaqImage}
      title={parentInfoFaqTitle}
      theme="light"
      titleSize="lg"
      listLength={list.length}
      fullHeight={false}
      // Divided sizes the image by list length, and three items gets its
      // shortest box. The content gets the same minimum so the section grows
      // with the image instead of letting it spill into the next band.
      imageClassName="lg:!h-[36rem]"
      contentClassName="lg:min-h-[36rem]"
    >
      <Disclosured
        list={list}
        initialActive={0}
        theme="light"
        buttonStyle="light"
      />
    </Divided>
  )
}
